import { useSelector } from "react-redux";
import { useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { PROXY_TYPES } from "../../../../utils/constants";
import { useTranslation } from "../../../../hooks";
import { errorToast, warningToast } from "../../../../utils/helpers/customToast.helper";
import { checkIpAgainstRanges } from "../../../../utils/validation/ipValidation";
import { ApiService } from "../../../../services/ApiService";
import { getG4Data } from "../../../../utils/helpers";
import { storeAnalytics } from "../../../../utils/helpers/storeAnalytics";
import { Settings } from "./components/Settings";
import { Details } from "./components/Details";
import { Preview } from "./components/Preview";
import "./NewOrder.scss";

export const NewOrder = ({ userBalance }) => {
  const { lang } = useParams();

  useEffect(() => window.scrollTo(0, 0), []);

  const {
    user,
    activeData,
    proxies: { fiveDollarLogick }
  } = useSelector((state) => state.content);

  const {
    notifications,
    prices,
    cabinet: { newOrder: { proxy } },
    main: { agreeWithRules: { warningMessage } }
  } = useTranslation();

  const initialSelectedProxyType = activeData?.proxy?.code
    ? { value: activeData.proxy.code, label: `${activeData.proxy.label} ${proxy}` }
    : { value: PROXY_TYPES.ipv4.code, label: `${PROXY_TYPES.ipv4.label} ${proxy}` };

  const methods = useForm({
    mode: "onSubmit",
    defaultValues: {
      proxyType: initialSelectedProxyType,
      policy: false
    }
  });

  const { proxyType } = methods.watch();

  const cfCaptchaRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);
  const [price, setPrice] = useState(null);
  const [cfToken, setCfToken] = useState("");
  const [selectedStep, setSelectedStep] = useState("settings");
  const [isAgreeWithConditions, setIsAgreeWithConditions] = useState(false);

  const isInvalidIpSelected = (data) => data?.auth?.value === "ip" && data?.authIp && checkIpAgainstRanges(data?.authIp);

  const isLowAmountForBalance = (data) => {
    const minAmountByBalance = 5;
    return fiveDollarLogick?.enabled && price?.usdAmount < minAmountByBalance && data?.payment?.code !== "balance";
  };

  const isLowBalance = (data) => data?.payment?.code === "balance" && user?.isAuth && price?.amount && userBalance?.balance < price?.amount;

  const isLowAmount = () => price && price?.minPrice && price?.amount < price?.minPrice;

  const combineParams = (data, token) => ({
      paymentSystemId: data?.payment?.id,
      currency: price?.currencyCode,
      countryId: data?.country?.id,
      count: +data?.quantity,
      days: data?.rent?.days,
      projectId: data?.asyncGoals?.value,
      projectCustom: data?.asyncGoals?.value,
      authType: data?.auth?.value,
      locale: lang,
      price: price?.price,
      captchaToken: token || "",
      amount: price?.amount || 0,
      authIp: data.authIp || "",
      proxyType: data?.proxyType?.value,
      proxyProtocol: data?.proxyProtocol?.value,
      modemRotationMin: data?.modemRotationMin?.min,
      mobileOperatorTag: data?.mobileOperator?.value,
      ga4Info: getG4Data(),
      clientEmail: user?.email || ""
    });

  const processOrderErrors = (data, status) => {
    if (data === "incorrect ip") throw notifications?.wrongIp;

    if (status < 200 || status >= 300) throw notifications.error;

    const errorStatuses = {
      202: notifications?.errorCaptcha,
      205: notifications?.errorUserPaymentSystem, //reload
      226: notifications.registerErrorWrongEmail
    };

    const errorMessage = errorStatuses[status];

    if (errorMessage) throw errorMessage;

    throw notifications.error;
  };

  const createOrder = async (data) => {
    setIsLoading(true);

    let token = cfToken;

    if (cfCaptchaRef?.current && !token) {
      token = await cfCaptchaRef.current?.getResponsePromise();
    }

    try {
      const params = combineParams(data, token, true);

      const { data: receivedData, status } = await ApiService.createPayment(params);

      if (receivedData && status === 200) {
        storeAnalytics(proxyType?.value);
        window.location.href = receivedData;

        return;
      }

      processOrderErrors(receivedData, status);
    } catch (err) {
      errorToast(err?.message || err, lang);
      setIsLoading(false);
    }
  };

  const getChecks = (data) => [
      {
        condition: !isAgreeWithConditions,
        message: warningMessage
      },
      {
        condition: isInvalidIpSelected(data),
        message: notifications?.invalidIp
      },
      {
        condition: isLowAmountForBalance(data),
        message: `${prices.lowAmount} ${prices.infoModalSelectBalance}`
      },
      {
        condition: isLowBalance(data),
        message: prices.lowBalance.main
      },
      {
        condition: isLowAmount(),
        message: `${notifications?.minPrice} ${price?.minPrice} ${price?.currencyCode}`
      }
    ];

  const onSubmit = (data) => {
    const checks = getChecks(data);

    for (const { condition, message } of checks) {
      if (condition) return warningToast(message, lang);
    }

    createOrder(data);
  };

  return (
    <FormProvider {...methods}>
      <form className="new-order" onSubmit={methods.handleSubmit(onSubmit)}>
        <div className="new-order__form">
          <Settings methods={methods} setSelectedStep={setSelectedStep} selectedStep={selectedStep} />
          <Details methods={methods} setSelectedStep={setSelectedStep} selectedStep={selectedStep} />
        </div>
        <Preview
          ref={cfCaptchaRef}
          methods={methods}
          price={price}
          setPrice={setPrice}
          setCfToken={setCfToken}
          setIsAgreeWithConditions={setIsAgreeWithConditions}
          isLoading={isLoading}
        />
      </form>
    </FormProvider>
  );
};
